import { planUpload, type AppendMetadata } from "../lib/upload-plan";
import {
  TransportError,
  type ArtifactActionId,
  type ArtifactDetail,
  type ArtifactListRow,
  type ArtifactLockRow,
  type ArtifactSearchRow,
  type ArtifactUnlockRow,
  type ClientRow,
  type DownloadLink,
  type ProjectRow,
  type StorageTransport,
  type UploadFileArgs,
  type UploadResult,
  type VersionRow,
} from "./types";

/**
 * Same-origin REST adapter over the live /api/v1 contract (spec-web-ui §2).
 * Credentials = the HttpOnly session cookie, carried by
 * `credentials: "include"` — the adapter never sees or stores a secret
 * (R23 §2(a): no static key in browser JS, ever). Browsers attach Origin
 * to non-GET fetches themselves, which is what the server's CSRF gate
 * (src/http/app-host.ts) checks on the cookie path.
 *
 * NO runtime zod here (spec §3 budget discipline): responses are trusted
 * to match the serializer shapes in ui/src/transport/types.ts — the
 * transport integration test (test/http/ui-transport.test.ts) pins them
 * over real TCP, so drift fails the suite at merge time, not in the
 * employee's browser.
 */

export interface RestTransportOptions {
  /** Server origin ("http://127.0.0.1:3100"). Default "" = same-origin
   * relative URLs — the only mode the browser shell uses. */
  baseUrl?: string;
  /** TEST SEAM: extra headers on every request. Real-TCP tests inject
   * `cookie` (Bun's fetch has no cookie jar) and `origin` (Bun's fetch
   * does not add Origin to POSTs the way browsers do). The browser shell
   * passes nothing here. */
  headers?: Record<string, string>;
  /**
   * The upload author seam (spec-web-ui §2: "autorius = sesijos
   * naudotojas, komponentas neklausia"): a thunk returning the CURRENT
   * session user's display name (shell passes name, email as fallback),
   * evaluated per upload so a re-login mid-session is always reflected.
   * Providing it is a prerequisite for `caps.upload` — an upload
   * capability with no author source would violate the wire contract
   * (src/domain/versions.ts metadata schemas require author). NOTE
   * (B-39): the value only SATISFIES the wire contract — for cookie
   * sessions the server derives the stored author from the session user
   * itself and overrides whatever this thunk answered; the authoritative
   * value comes back in the 201 response (UploadResult.author).
   */
  sessionAuthor?: () => string | null;
  /**
   * The session scope seam (B-39 ride-along): a thunk returning the
   * CURRENT session user's permission scope (the shell passes
   * /api/auth/me's `scope`), or null when no user is known. When wired,
   * `caps.upload` is true only for a known non-`read` scope — read-scope
   * users never see the upload affordance. Absent thunk = pre-B-39
   * behavior (author seam alone decides). Either way the server's domain
   * write boundary (403 `insufficient_scope`) stays the enforcement
   * backstop — this seam is UX, not security.
   */
  sessionScope?: () => "read" | "read_write" | null;
}

/** The registry error envelope — src/http/errors.ts errorBody. */
interface ErrorEnvelope {
  error?: { code?: string; message?: string; details?: unknown };
}

/** Narrow structural view of `document` for openExternal: this module is
 * also compiled under the ROOT tsconfig (no DOM lib) via the transport
 * integration test, so it must not reference DOM lib types. */
interface NavigationDocument {
  createElement(tag: string): {
    href: string;
    rel: string;
    click(): void;
    remove(): void;
  };
  body: { appendChild(node: unknown): void };
}

/** Narrow structural view of XMLHttpRequest, for the same no-DOM-lib
 * reason as {@link NavigationDocument}. Only what the upload path uses. */
interface XhrLike {
  open(method: string, url: string): void;
  setRequestHeader(name: string, value: string): void;
  send(body: unknown): void;
  abort(): void;
  withCredentials: boolean;
  status: number;
  responseText: string;
  upload: {
    onprogress:
      | ((e: { lengthComputable: boolean; loaded: number; total: number }) => void)
      | null;
  };
  onload: (() => void) | null;
  onerror: (() => void) | null;
  onabort: (() => void) | null;
}

type XhrConstructor = new () => XhrLike;

/** A settled form-upload response, transport-mechanism-agnostic. */
interface FormResponse {
  status: number;
  bodyText: string;
}

function abortError(): TransportError {
  return new TransportError("upload_aborted", "upload was aborted by the caller");
}

export class RestTransport implements StorageTransport {
  /** `upload` requires an author source (the `sessionAuthor` option) and,
   * when a `sessionScope` seam is wired, a known non-`read` scope — a
   * LIVE getter, so a login/scope change is reflected on the next render
   * (see StorageTransport.caps and both options' docblocks). Components
   * render no upload UI when this is false. `actions` is the per-artifact
   * action set: the browser fulfils only "download" (mint presigned link →
   * native navigation), so it advertises exactly that. */
  readonly caps: {
    readonly upload: boolean;
    readonly actions: readonly ArtifactActionId[];
  };

  private readonly baseUrl: string;
  private readonly headers: Record<string, string>;
  private readonly sessionAuthor?: () => string | null;

  constructor(options: RestTransportOptions = {}) {
    this.baseUrl = (options.baseUrl ?? "").replace(/\/+$/, "");
    this.headers = options.headers ?? {};
    this.sessionAuthor = options.sessionAuthor;
    const { sessionAuthor, sessionScope } = options;
    this.caps = {
      // The browser fulfils the "download" intent (getDownloadLink →
      // openExternal); open/import are a later adapter's actions.
      actions: ["download"] as const,
      get upload(): boolean {
        if (sessionAuthor === undefined) return false;
        if (sessionScope === undefined) return true;
        const scope = sessionScope();
        // Unknown user (null) hides the affordance; mirror the domain
        // write boundary otherwise (only an explicit "read" refuses).
        return scope !== null && scope !== "read";
      },
    };
  }

  async listClients(): Promise<ClientRow[]> {
    return this.request<ClientRow[]>("GET", "/api/v1/clients");
  }

  async listProjects(clientId: string): Promise<ProjectRow[]> {
    return this.request<ProjectRow[]>(
      "GET",
      `/api/v1/clients/${encodeURIComponent(clientId)}/projects`,
    );
  }

  async listArtifacts(args: { projectId: string; q?: string }): Promise<ArtifactListRow[]> {
    const qs = args.q ? `?q=${encodeURIComponent(args.q)}` : "";
    return this.request<ArtifactListRow[]>(
      "GET",
      `/api/v1/projects/${encodeURIComponent(args.projectId)}/artifacts${qs}`,
    );
  }

  async searchArtifacts(q: string): Promise<ArtifactSearchRow[]> {
    return this.request<ArtifactSearchRow[]>(
      "GET",
      `/api/v1/artifacts/search?q=${encodeURIComponent(q)}`,
    );
  }

  async getArtifact(id: string): Promise<ArtifactDetail> {
    return this.request<ArtifactDetail>("GET", `/api/v1/artifacts/${encodeURIComponent(id)}`);
  }

  /**
   * `POST /api/v1/artifacts/:id/lock` (spec-artifact-locking §7). The body
   * is sent ONLY when a note exists: the route accepts a bodyless request
   * and parses JSON only when the content-type says so
   * (src/routes/artifacts.ts parseLockBodyOrThrow), so an empty `{}` would
   * be pure noise on the wire.
   */
  async lockArtifact(artifactId: string, note?: string): Promise<ArtifactLockRow> {
    const trimmed = note?.trim();
    return this.request<ArtifactLockRow>(
      "POST",
      `/api/v1/artifacts/${encodeURIComponent(artifactId)}/lock`,
      trimmed ? { note: trimmed } : undefined,
    );
  }

  /** `DELETE /api/v1/artifacts/:id/lock` — idempotent; the response echoes
   * the released holder (`previousLockedBy`), which is what lets a takeover
   * report whose lock it replaced. */
  async unlockArtifact(artifactId: string): Promise<ArtifactUnlockRow> {
    return this.request<ArtifactUnlockRow>(
      "DELETE",
      `/api/v1/artifacts/${encodeURIComponent(artifactId)}/lock`,
    );
  }

  async getDownloadLink(id: string, versionNumber?: number): Promise<DownloadLink> {
    let n = versionNumber;
    if (n === undefined) {
      // Task grammar: "the newest one" is the adapter's job to resolve.
      // The detail response carries the current pointer; fall back to the
      // highest number (versions are versionNumber ASC on the wire).
      const detail = await this.getArtifact(id);
      const current = detail.versions.find((v) => v.id === detail.currentVersionId);
      n = current?.versionNumber ?? detail.versions[detail.versions.length - 1]?.versionNumber;
      if (n === undefined) {
        throw new TransportError("version_not_found", "artifact has no versions", 404);
      }
    }
    return this.request<DownloadLink>(
      "POST",
      `/api/v1/artifacts/${encodeURIComponent(id)}/versions/${n}/download-link`,
    );
  }

  /**
   * Same-origin multipart upload onto the EXISTING endpoints, cookie
   * session (spec-web-ui §2 "Upload"; owner decision 2026-07-21 — no
   * presigned upload in this stage):
   *
   *   create → POST /api/v1/projects/:id/artifacts   (src/routes/artifacts.ts:163-184)
   *   append → PUT  /api/v1/artifacts/:id/versions   (src/routes/artifacts.ts:187-202)
   *
   * Semantics mirror MCP `upload_artifact` (mcp/handlers.ts:597-675): the
   * adapter lists the project's artifacts and matches the file name
   * EXACTLY — one match appends a version, none creates, several refuses
   * (`artifact_name_ambiguous`). The component stays dumb; its pre-send
   * confirm uses the same pure planner (ui/src/lib/upload-plan.ts) but THIS
   * check is the authoritative one, at send time. EXCEPT when the caller
   * names its target (`args.artifactId` — a RETURN): then there is nothing
   * to resolve and the write goes straight to that artifact
   * ({@link appendToArtifact}).
   *
   * Wire contract (src/http/multipart.ts module header): EXACTLY two parts
   * in FIXED order — "metadata" (JSON, ≤1 MiB) then "file" (streamed
   * server-side). FormData serializes entries in insertion order (WHATWG
   * spec; the server's own tests build bodies the same way —
   * test/http/helpers.ts multipartBody), so appending metadata first IS
   * the ordering guarantee.
   *
   * AUTHOR (B-39, owner decision 2026-07-21): metadata.author is still
   * sent (it is a required wire field for every client), but for cookie
   * sessions the SERVER derives the stored author from the session user
   * and overrides the sent value (src/domain/versions.ts
   * applyActorAuthorOverride). The 201 response carries the stored
   * author; UploadResult.author surfaces it so UI never shows the local
   * guess.
   *
   * DECISION — no X-Content-Sha256 from the browser: the optional
   * integrity header exists server-side (src/domain/versions.ts
   * checkDeclaredHashOrThrow), but hashing a File in the browser means
   * reading the WHOLE file into memory before sending (WebCrypto digest
   * is one-shot; 500 MB policy files would double-buffer), and the server
   * computes + stores the sha256 during the streaming put anyway
   * (src/blob/store.ts). Byte integrity is verified end-to-end by the
   * download-side hash checks instead.
   *
   * Two send mechanisms behind one method:
   *   - XMLHttpRequest when the environment has it (real browsers):
   *     upload.onprogress → onProgress, abort via signal → xhr.abort().
   *   - fetch fallback (Bun has no XHR): no upload progress events —
   *     onProgress is simply never called (indeterminate UI) — but abort
   *     still works via the same AbortSignal. This keeps the method
   *     testable under bun and working in any XHR-less host.
   */
  async uploadFile(args: UploadFileArgs): Promise<UploadResult> {
    const file = args.file;
    if (!file) {
      // UploadFileArgs.file is optional ONLY for the SketchUp intent path
      // (bytes are Ruby-mediated there). On REST the file IS the byte
      // source — the component's "file" source mode always passes one, so
      // reaching this is a programming error, not a user outcome
      // (spec-upload-unification §3; status null, no request sent).
      throw new TransportError("internal_error", "uploadFile on the REST transport requires a file");
    }
    if (!this.sessionAuthor) {
      // caps.upload is false in this configuration, so components never
      // surface a path that reaches this — programming-error backstop.
      throw new TransportError("not_implemented", "upload requires a sessionAuthor seam");
    }
    const author = this.sessionAuthor();
    if (!author) {
      throw new TransportError(
        "validation_failed",
        "session user unknown — cannot attach an upload author",
      );
    }

    // A RETURN names its target (UploadFileArgs.artifactId): the id, not the
    // name, is the authority — see appendToArtifact.
    if (args.artifactId !== undefined) {
      if (args.confirmedKind === "create") {
        // Contradictory caller state: a return targets an EXISTING artifact,
        // so a confirm that said "create" cannot belong to this upload. The
        // only way to reach it is a caller whose confirm and target came
        // from different picks — the desync class the component's
        // PendingUpload.returnTo snapshot exists to prevent. Refuse loudly
        // instead of silently trusting one half (status null, no request).
        throw new TransportError(
          "internal_error",
          'uploadFile got an artifactId (a RETURN) with confirmedKind "create" — inconsistent caller state',
        );
      }
      return this.appendToArtifact(args.artifactId, file, author, args);
    }

    // Authoritative create-vs-append decision (full, unfiltered list —
    // same input the MCP handler matches against, mcp/handlers.ts:610).
    const rows = await this.listArtifacts({ projectId: args.projectId });
    const plan = planUpload(rows, file.name, author, args.message, args.baseVersionNumber);
    if (plan.kind === "ambiguous") {
      throw new TransportError(
        "artifact_name_ambiguous",
        `${plan.artifactIds.length} artifacts named "${plan.artifactName}" exist in this project`,
      );
    }
    if (args.confirmedKind !== undefined && args.confirmedKind !== plan.kind) {
      // The world moved between the caller's confirm and this send, so the
      // re-decision would take a DIFFERENT path than the one the caller
      // prepared. Refusing (no bytes sent) is what keeps the append path
      // inside the take-for-work discipline: a create→append flip would
      // append onto a colleague's brand-new artifact with neither the
      // auto-lock claim nor a base version — the only silent write outside
      // the locking rules (spec-artifact-locking §8) — and an append→create
      // flip would drop a base guard the caller had already declared.
      // Recovery is a fresh pick: the caller re-plans, auto-locks and
      // declares a base for whatever the file resolves to now.
      throw new TransportError(
        "upload_plan_changed",
        `upload of "${file.name}" was confirmed as "${args.confirmedKind}" but now resolves to "${plan.kind}"`,
      );
    }

    const form = new FormData();
    form.append("metadata", JSON.stringify(plan.metadata)); // part 1 — MUST be first
    form.append("file", file, file.name); // part 2 — streamed server-side

    const [method, path] =
      plan.kind === "create"
        ? (["POST", `/api/v1/projects/${encodeURIComponent(args.projectId)}/artifacts`] as const)
        : (["PUT", `/api/v1/artifacts/${encodeURIComponent(plan.artifactId)}/versions`] as const);

    const res = await this.sendForm(method, path, form, args.onProgress, args.signal);
    if (res.status < 200 || res.status >= 300) {
      throw transportErrorFromText(res.status, res.bodyText);
    }

    if (plan.kind === "create") {
      // 201 body: serializeArtifact + versions:[serializeVersion] —
      // src/routes/artifacts.ts:176-182.
      const body = JSON.parse(res.bodyText) as ArtifactDetail;
      const v1 = body.versions[0];
      if (!v1) {
        throw new TransportError("internal_error", "create response carried no version", res.status);
      }
      return {
        artifactId: body.id,
        artifactName: body.name,
        versionNumber: v1.versionNumber,
        author: v1.author, // the STORED author (server-derived on cookie paths, B-39)
      };
    }
    // 201 body: serializeVersion (carries artifactId) — src/routes/artifacts.ts:200.
    const version = JSON.parse(res.bodyText) as VersionRow;
    return {
      artifactId: version.artifactId,
      artifactName: plan.artifactName,
      versionNumber: version.versionNumber,
      author: version.author, // the STORED author (server-derived on cookie paths, B-39)
    };
  }

  /**
   * The RETURN write (spec-artifact-locking §8; channel bug report 127d):
   * append onto the artifact the caller NAMED, with no name resolution at
   * all. The kind is `append` by definition — a return has an artifact to
   * return to — so `planUpload`, the ambiguity refusal and the
   * `confirmedKind` cross-check are all skipped; keeping them would let a
   * project holding `dd.skp` next to a locked `dd` write the user's work to
   * the wrong (unlocked) file, or refuse the return outright when two
   * artifacts share the name.
   *
   * The name is read from the artifact ITSELF (one GET, replacing the list
   * GET the name path made — same round-trip count) rather than from
   * `file.name`, because on this path the two legitimately differ; a target
   * that no longer exists fails right here with the server's
   * `artifact_not_found` instead of degrading into a create. The server's
   * own guards (`artifact_locked`, `version_conflict`) stay authoritative
   * on the PUT.
   */
  private async appendToArtifact(
    artifactId: string,
    file: File,
    author: string,
    args: UploadFileArgs,
  ): Promise<UploadResult> {
    const target = await this.getArtifact(artifactId);
    // Byte-identical to planUpload's append branch (trimmed message omitted
    // when empty, base omitted when undeclared) — one metadata shape.
    const metadata: AppendMetadata = { author };
    const message = args.message?.trim();
    if (message) metadata.message = message;
    if (args.baseVersionNumber !== undefined) {
      metadata.baseVersionNumber = args.baseVersionNumber;
    }

    const form = new FormData();
    form.append("metadata", JSON.stringify(metadata)); // part 1 — MUST be first
    form.append("file", file, file.name); // part 2 — streamed server-side

    const res = await this.sendForm(
      "PUT",
      `/api/v1/artifacts/${encodeURIComponent(artifactId)}/versions`,
      form,
      args.onProgress,
      args.signal,
    );
    if (res.status < 200 || res.status >= 300) {
      throw transportErrorFromText(res.status, res.bodyText);
    }
    // 201 body: serializeVersion — src/routes/artifacts.ts:200.
    const version = JSON.parse(res.bodyText) as VersionRow;
    return {
      artifactId: version.artifactId,
      artifactName: target.name,
      versionNumber: version.versionNumber,
      author: version.author, // the STORED author (server-derived on cookie paths, B-39)
    };
  }

  /** Route the form to XHR (browser) or fetch (everything else). */
  private sendForm(
    method: "POST" | "PUT",
    path: string,
    form: FormData,
    onProgress?: (sentBytes: number, totalBytes: number) => void,
    signal?: AbortSignal,
  ): Promise<FormResponse> {
    const Xhr = (globalThis as { XMLHttpRequest?: XhrConstructor }).XMLHttpRequest;
    const url = `${this.baseUrl}${path}`;
    return Xhr
      ? this.sendFormXhr(Xhr, method, url, form, onProgress, signal)
      : this.sendFormFetch(method, url, form, signal);
  }

  private sendFormXhr(
    Xhr: XhrConstructor,
    method: "POST" | "PUT",
    url: string,
    form: FormData,
    onProgress?: (sentBytes: number, totalBytes: number) => void,
    signal?: AbortSignal,
  ): Promise<FormResponse> {
    return new Promise<FormResponse>((resolve, reject) => {
      if (signal?.aborted) {
        reject(abortError());
        return;
      }
      const xhr = new Xhr();
      xhr.open(method, url);
      // The cookie rides withCredentials; the browser sets the multipart
      // Content-Type (with boundary) itself. The extra headers seam is
      // test-only (real browsers forbid setting Cookie via XHR — tests
      // exercise this path with a fake XHR under bun).
      xhr.withCredentials = true;
      for (const [name, value] of Object.entries(this.headers)) {
        xhr.setRequestHeader(name, value);
      }
      const onSignalAbort = (): void => xhr.abort();
      const cleanup = (): void => signal?.removeEventListener("abort", onSignalAbort);
      signal?.addEventListener("abort", onSignalAbort);
      if (onProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) onProgress(e.loaded, e.total);
        };
      }
      xhr.onload = () => {
        cleanup();
        resolve({ status: xhr.status, bodyText: xhr.responseText });
      };
      xhr.onerror = () => {
        cleanup();
        reject(new TransportError("network_error", "upload request failed"));
      };
      xhr.onabort = () => {
        cleanup();
        reject(abortError());
      };
      xhr.send(form);
    });
  }

  private async sendFormFetch(
    method: "POST" | "PUT",
    url: string,
    form: FormData,
    signal?: AbortSignal,
  ): Promise<FormResponse> {
    let res: Response;
    try {
      res = await fetch(url, {
        method,
        credentials: "include",
        headers: this.headers,
        body: form,
        signal,
      });
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") {
        throw abortError();
      }
      throw new TransportError(
        "network_error",
        err instanceof Error ? err.message : "request failed",
      );
    }
    return { status: res.status, bodyText: await res.text() };
  }

  openExternal(url: string): void {
    const doc = (globalThis as { document?: NavigationDocument }).document;
    if (!doc) {
      throw new TransportError("not_implemented", "openExternal requires a browser environment");
    }
    // Anchor navigation, not window.open: the presigned /dl response is
    // Content-Disposition: attachment, so this triggers the browser's
    // native download UI without leaving (or blanking) the app page.
    const a = doc.createElement("a");
    a.href = url;
    a.rel = "noopener";
    doc.body.appendChild(a);
    a.click();
    a.remove();
  }

  /** One JSON round trip. `body` is serialized only when given — the lock
   * routes accept bodyless POST/DELETE, and adding a content-type without a
   * body would make the server try to parse one. */
  private async request<T>(
    method: "GET" | "POST" | "DELETE",
    path: string,
    body?: unknown,
  ): Promise<T> {
    let res: Response;
    try {
      res = await fetch(`${this.baseUrl}${path}`, {
        method,
        credentials: "include",
        headers:
          body === undefined
            ? this.headers
            : { ...this.headers, "content-type": "application/json" },
        body: body === undefined ? undefined : JSON.stringify(body),
      });
    } catch (err) {
      throw new TransportError(
        "network_error",
        err instanceof Error ? err.message : "request failed",
      );
    }
    if (!res.ok) {
      throw await toTransportError(res);
    }
    return (await res.json()) as T;
  }
}

/** Map a non-2xx response to the registry-code shape. A body that is not
 * the `{ error: { code, message } }` envelope (proxy error page, empty
 * body) degrades honestly to `internal_error` with the HTTP status kept. */
async function toTransportError(res: Response): Promise<TransportError> {
  return transportErrorFromText(res.status, await res.text().catch(() => ""));
}

/** Same mapping from already-read body text (the XHR path has no Response). */
function transportErrorFromText(status: number, bodyText: string): TransportError {
  let envelope: ErrorEnvelope | null = null;
  try {
    envelope = JSON.parse(bodyText) as ErrorEnvelope;
  } catch {
    envelope = null;
  }
  const code = envelope?.error?.code ?? "internal_error";
  const message = envelope?.error?.message ?? `HTTP ${status}`;
  // `details` travels verbatim: the locking refusals put the holder /
  // conflicting version there, and the UI composes its LT text from it
  // (ui/src/lib/locking.ts). Absent for most codes — that is fine, the
  // consumer falls back to the generic gloss.
  return new TransportError(code, message, status, envelope?.error?.details);
}
